'use client';

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { UnifiedModal } from '@/app_components/ui/UnifiedModal';
import {
  deleteFortuneReleaseCookie,
  getFortuneReleaseCookie,
  setFortuneReleaseCookie,
} from '@/app_lib/utils/fortuneReleaseCookie';

interface FortuneReleaseModalRef {
  openModal: () => void;
}

interface FortuneReleaseModalProps {
  trigger?: React.ReactNode;
}

const FortuneReleaseModal = forwardRef<FortuneReleaseModalRef, FortuneReleaseModalProps>(({ trigger }, ref) => {
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('release-candidate');
  const [isOpen, setIsOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal: () => setIsOpen(true),
  }));

  useEffect(() => {
    // Get current cookie value on component mount
    const value = getFortuneReleaseCookie();
    setCurrentValue(value);
  }, []);

  const handleSetCookie = () => {
    if (inputValue.trim()) {
      setFortuneReleaseCookie(inputValue.trim());
      setCurrentValue(inputValue.trim());
      // Refresh the page to get content from the new backend
      window.location.reload();
    }
  };

  const handleDeleteCookie = () => {
    deleteFortuneReleaseCookie();
    setCurrentValue(null);
    // Refresh the page to return to production backend
    window.location.reload();
  };

  const modalContent = (
    <div className='p-2 space-y-4'>
      <div className='space-y-2'>
        <div className='text-sm font-medium text-gray-700'>Current Fortune Cookie Value:</div>
        <div className='text-sm text-gray-600 bg-gray-50 p-2 rounded border min-h-[40px] flex items-center'>
          {currentValue ? (
            <code className='font-mono'>{currentValue}</code>
          ) : (
            <span className='italic'>Not set (using production backend)</span>
          )}
        </div>
      </div>

      <div className='space-y-2'>
        <label
          className='text-sm font-medium text-gray-700'
          htmlFor='cookie-value'
        >
          Set Fortune Cookie Value:
        </label>
        <input
          className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
          id='cookie-value'
          onChange={(e) => setInputValue(e.target.value)}
          placeholder='Enter backend route value'
          type='text'
          value={inputValue}
        />
        <p className='text-xs text-gray-500'>This will route traffic to the specified backend service</p>
      </div>
      <p className='text-xs text-gray-500'>Page will refresh automatically after setting or deleting the cookie</p>
    </div>
  );

  return (
    <>
      {trigger && trigger}
      <div
        className='contents'
        data-fortune-release-modal
      >
        <UnifiedModal
          className='fortune-release-modal'
          firstButton={{ dataCy: 'setCookieBtn', onClick: handleSetCookie, title: 'Set Cookie & Refresh' }}
          isOpen={isOpen}
          onOpenChange={(open) => setIsOpen(open)}
          secondButton={{ dataCy: 'deleteCookieBtn', onClick: handleDeleteCookie, title: 'Delete Cookie & Refresh' }}
          title='Fortune Cookie Release Manager'
        >
          {modalContent}
        </UnifiedModal>
      </div>
    </>
  );
});

FortuneReleaseModal.displayName = 'FortuneReleaseModal';

// biome-ignore lint: Component must export its ref type interface alongside
export { FortuneReleaseModal, type FortuneReleaseModalRef };
